/**
 * Typed client for the telemetry-lake support API (`/api/telemetry/*`).
 *
 * This is the COLD layer: S3 Parquet read through Athena, minutes-to-hours
 * behind. The Redis hot logs (`/api/logs`) are a different question and a
 * different page — nothing here replaces them.
 *
 * Two things every caller must know:
 *
 *   1. **202 is normal.** Athena is asynchronous; the backend waits up to its
 *      inline deadline and hands back a query id when the query outlives it.
 *      `runTelemetryQuery` polls `/queries/<id>` and resolves with the SAME
 *      document the inline path would have returned, so callers never write
 *      two readers for one endpoint.
 *   2. **Every scan costs money.** No polling, no background refetch, no
 *      prefetch. `meta.query` travels with every response (cache hits
 *      included) and the UI shows it.
 *
 * `unit_bucket` is never a client input — the backend derives it from unit_id.
 */
import { HttpError, httpWithStatus } from "@/lib/http";

const BASE = "/api/telemetry";

/** `/diagnostics` scans one window; wider ranges must use the per-view endpoints. */
export const DIAGNOSTICS_MAX_HOURS = 48;
/** `/coverage` must switch to day granularity past this. */
export const COVERAGE_HOUR_GRANULARITY_MAX_HOURS = 7 * 24;
/** `/raw-window` never spans more than this. */
export const RAW_WINDOW_MAX_MINUTES = 60;

export type TelemetryEventKind =
  | "state_change"
  | "command"
  | "anomaly"
  | "gap"
  | "sample"
  /** DBG/EKM/rejected frames — the charger talking about itself, not telemetry. */
  | "service";
/**
 * A metric the lake itself can aggregate — the only values `metrics=` may carry.
 *
 * `session_energy` is a column in `telemetry_raw` and is already whitelisted in
 * the backend's `SERIES_METRICS`, so asking for it costs nothing extra: the
 * compact scan reads the row anyway and the wide statement reads the column
 * anyway.
 */
export type TelemetryMetric =
  | "current"
  | "voltage"
  | "temperature"
  | "frequency"
  | "session_energy";

/**
 * A metric the charts can draw. `power` is not archived — it is voltage x
 * current, derived from series points already in memory — so it must never
 * reach a `metrics=` parameter.
 */
export type TelemetryChartMetric = TelemetryMetric | "power";
export type CoverageStatus = "full" | "partial" | "empty" | "not_archived" | "unknown";

/* ------------------------------------------------------------------ */
/*  Envelope                                                           */
/* ------------------------------------------------------------------ */

export interface TelemetryQueryMeta {
  athena_query_id: string | null;
  bytes_scanned: number;
  billed_bytes: number;
  estimated_cost_usd: number;
  duration_ms: number;
  /** miss | bundle | result_reuse */
  cache: string;
  engine: string | null;
}

export interface TelemetryLakeMeta {
  watermark_ts: string | null;
  lag_seconds: number | null;
  status_available: boolean;
  status_stale: boolean;
  pending_files: number | null;
  sampling_rate: number;
  unit_bucket: string | null;
  unit_bucket_count: number;
  /** true | false | null — null means "unknown", which is NOT a mismatch. */
  config_match: boolean | null;
}

export interface TelemetryBudgetMeta {
  spent_bytes_today: number;
  limit_bytes: number;
  install_spent_bytes_today?: number;
  install_limit_bytes?: number;
}

export interface TelemetryMeta {
  lake: TelemetryLakeMeta;
  unit: { reporting_interval_s: number; offline_threshold_s: number };
  truncated: boolean;
  /** compact | wide | point */
  mode: string;
  query: TelemetryQueryMeta;
  budget: TelemetryBudgetMeta;
  bucket_seconds?: number;
  total_events?: number;
}

export interface TelemetryEnvelope<T> {
  unit_id: string;
  range: { from: string; to: string };
  data: T;
  next_cursor: string | null;
  bundle_key?: string;
  meta: TelemetryMeta;
}

/** The 202 body. Shared by every endpoint that can outlive the inline deadline. */
export interface TelemetryRunning {
  status: "running";
  athena_query_id: string;
  poll_after_ms: number;
  meta: { query: TelemetryQueryMeta };
}

/* ------------------------------------------------------------------ */
/*  Views                                                              */
/* ------------------------------------------------------------------ */

export interface StateChangeDetail {
  from: string | null;
  to: string | null;
  prev_duration_s: number | null;
}

export interface CommandDetail {
  raw: string | null;
  /** protocol.parse_command output — decoded server-side, never re-implemented here. */
  decoded: Record<string, unknown> | null;
}

export interface GapDetail {
  seconds: number;
  /** offline_looking = past the platform's own offline rule (interval × 10). */
  severity: "offline_looking" | "short";
  short_threshold_s: number;
  offline_threshold_s: number;
  prev_ts: string;
}

export interface AnomalyDetail {
  rule: string;
  severity: "error" | "warn";
  first_ts: string;
  last_ts: string;
  sample_count: number;
  observed: Record<string, number | string[]>;
}

/**
 * A frame that is not a measurement, kept because support still needs to see it.
 *
 * `marker` is what the archive itself carries: `DBG` / `EKM` in the state column
 * for frames `parse_telemetry` never parses, `REJECTED` in the command column
 * for a frame whose checksum did not validate.
 */
export interface ServiceDetail {
  marker: string;
  command: string | null;
}

export type TelemetryEventDetail =
  | StateChangeDetail
  | CommandDetail
  | GapDetail
  | AnomalyDetail
  | ServiceDetail
  | null;

export interface TelemetryEventRef {
  src_file: string | null;
  src_line: number | null;
}

export interface TelemetryEvent {
  ts: string;
  kind: TelemetryEventKind;
  state: string | null;
  voltage: number | null;
  current: number | null;
  temperature: number | null;
  frequency: number | null;
  session_energy: number | null;
  lifetime_energy: number | null;
  ref: TelemetryEventRef;
  detail: TelemetryEventDetail;
  /** Always present, always null unless the caller was allowed raw frames. */
  raw_message: string | null;
}

export interface CoverageCell {
  dt: string;
  /** Only on granularity=hour. */
  hour?: string;
  samples: number;
  expected: number;
  completeness: number | null;
  first_ts: string | null;
  last_ts: string | null;
  states: string[];
  commands: number;
  errors: number;
  status: CoverageStatus;
}

export interface SeriesMetricPoint {
  avg: number;
  min: number;
  max: number;
}

export interface SeriesPoint {
  ts: string;
  samples: number;
  state_dominant: string | null;
  current?: SeriesMetricPoint | null;
  voltage?: SeriesMetricPoint | null;
  temperature?: SeriesMetricPoint | null;
  frequency?: SeriesMetricPoint | null;
  /** Watt-hours as archived, cumulative within a session and reset between them. */
  session_energy?: SeriesMetricPoint | null;
}

export interface TelemetrySummary {
  samples: number;
  /** How many of `samples` were service frames rather than telemetry. */
  service_frames?: number;
  first_ts: string | null;
  /** "Last archived", never "last seen" — the lake lags. */
  last_archived_ts: string | null;
  last_state: string | null;
  last_voltage: number | null;
  last_current: number | null;
  last_temperature: number | null;
  state_changes: number;
  commands: number;
  gaps: number;
  anomalies: number;
  anomalies_error: number;
}

export interface RawFrameRow {
  ts_utc: string;
  ts_local_raw?: string | null;
  state: string | null;
  voltage: number | null;
  current: number | null;
  temperature: number | null;
  frequency: number | null;
  session_energy: number | null;
  lifetime_energy: number | null;
  command: string | null;
  raw_message?: string | null;
  src_file: string | null;
  src_line: number | null;
}

export interface DiagnosticsResponse {
  unit_id: string;
  range: { from: string; to: string };
  coverage: CoverageCell[];
  events: TelemetryEvent[];
  series: SeriesPoint[];
  summary: TelemetrySummary;
  next_cursor: string | null;
  bundle_key?: string;
  meta: TelemetryMeta;
}

export type EventsResponse = TelemetryEnvelope<TelemetryEvent[]>;
export type CoverageResponse = TelemetryEnvelope<CoverageCell[]>;
export type SeriesResponse = TelemetryEnvelope<SeriesPoint[]>;
export type RawWindowResponse = TelemetryEnvelope<RawFrameRow[]>;

export interface LakeStatusResponse {
  available: boolean;
  stale: boolean;
  watermark_ts: string | null;
  lag_seconds: number | null;
  status_age_seconds: number | null;
  backlog: { pending_files?: number; pending_bytes?: number; [k: string]: unknown };
  lake: Record<string, unknown>;
  feeder: Record<string, unknown>;
  last_batch: Record<string, unknown> | null;
  config_match: boolean | null;
  mismatch: Record<string, unknown> | null;
  warnings: string[];
  reason: string | null;
  sampling_rate: number;
  config: Record<string, unknown>;
  athena: { enabled: boolean; reachable: boolean | null };
  budget: TelemetryBudgetMeta;
}

/* ------------------------------------------------------------------ */
/*  Errors                                                             */
/* ------------------------------------------------------------------ */

/** Machine-readable `error` code from the API body, or null for non-API failures. */
export function telemetryErrorCode(err: unknown): string | null {
  if (err instanceof HttpError && err.body && typeof err.body === "object") {
    const code = (err.body as { error?: unknown }).error;
    if (typeof code === "string") return code;
  }
  return null;
}

/** The API's human-facing sentence; falls back to the exception message. */
export function telemetryErrorMessage(err: unknown): string {
  if (err instanceof HttpError && err.body && typeof err.body === "object") {
    const message = (err.body as { message?: unknown }).message;
    if (typeof message === "string" && message) return message;
  }
  return err instanceof Error && err.message ? err.message : "Telemetry query failed.";
}

/** Full error body — carries the code-specific detail (limits, mismatch, ids). */
export function telemetryErrorDetail(err: unknown): Record<string, unknown> {
  if (err instanceof HttpError && err.body && typeof err.body === "object") {
    return err.body as Record<string, unknown>;
  }
  return {};
}

export class TelemetryPollTimeout extends Error {
  athenaQueryId: string;
  constructor(athenaQueryId: string) {
    super("The telemetry query is still running. Narrow the range and try again.");
    this.name = "TelemetryPollTimeout";
    this.athenaQueryId = athenaQueryId;
  }
}

/* ------------------------------------------------------------------ */
/*  Request plumbing                                                   */
/* ------------------------------------------------------------------ */

export interface TelemetryRequestOptions {
  signal?: AbortSignal;
  /** Called when a request goes async, so the UI can say "query running". */
  onQueryRunning?: (athenaQueryId: string) => void;
  /** Give up polling after this long. Default 2 min. */
  maxPollMs?: number;
}

const MAX_POLL_INTERVAL_MS = 5_000;

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    }
    if (signal?.aborted) {
      onAbort();
      return;
    }
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

function isRunning(status: number, body: unknown): body is TelemetryRunning {
  return status === 202 && !!body && typeof body === "object"
    && typeof (body as TelemetryRunning).athena_query_id === "string";
}

/**
 * GET a telemetry endpoint, transparently collecting a 202.
 *
 * A slow query is a latency accident, not a different endpoint — the polled
 * document is the inline one, so this returns `T` either way.
 */
export async function runTelemetryQuery<T>(
  url: string,
  opts: TelemetryRequestOptions = {},
): Promise<T> {
  const { signal, onQueryRunning, maxPollMs = 120_000 } = opts;
  const first = await httpWithStatus<T | TelemetryRunning>(url, { signal });
  if (!isRunning(first.status, first.data)) return first.data as T;

  const queryId = first.data.athena_query_id;
  onQueryRunning?.(queryId);

  let delay = Math.max(250, first.data.poll_after_ms || 1000);
  const deadline = Date.now() + maxPollMs;
  const pollUrl = `${BASE}/queries/${encodeURIComponent(queryId)}`;

  for (;;) {
    await sleep(delay, signal);
    const next = await httpWithStatus<T | TelemetryRunning>(pollUrl, { signal });
    if (!isRunning(next.status, next.data)) return next.data as T;
    if (Date.now() >= deadline) throw new TelemetryPollTimeout(queryId);
    delay = Math.min(Math.round(delay * 1.5), MAX_POLL_INTERVAL_MS);
  }
}

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

/* ------------------------------------------------------------------ */
/*  Endpoints                                                          */
/* ------------------------------------------------------------------ */

/** Admin-only. Watermark, feeder backlog, config match, budget. */
export function fetchLakeStatus(opts?: TelemetryRequestOptions): Promise<LakeStatusResponse> {
  return runTelemetryQuery<LakeStatusResponse>(`${BASE}/lake/status`, opts);
}

export interface DiagnosticsParams {
  from: string;
  to: string;
  kinds?: TelemetryEventKind[];
  metrics?: TelemetryMetric[];
  limit?: number;
  bucket?: string;
}

export function fetchDiagnostics(
  unitId: string,
  params: DiagnosticsParams,
  opts?: TelemetryRequestOptions,
): Promise<DiagnosticsResponse> {
  const url = `${BASE}/units/${encodeURIComponent(unitId)}/diagnostics` + qs({
    from: params.from,
    to: params.to,
    kinds: params.kinds?.join(","),
    metrics: params.metrics?.join(","),
    limit: params.limit,
    bucket: params.bucket,
  });
  return runTelemetryQuery<DiagnosticsResponse>(url, opts);
}

export interface EventsParams {
  from: string;
  to: string;
  kinds?: TelemetryEventKind[];
  states?: string[];
  limit?: number;
  order?: "asc" | "desc";
  cursor?: string | null;
  /** Admin + window ≤ 15 min only; the backend rejects anything else. */
  include_raw?: boolean;
  /** Admin escape hatch for ranges past the 7-day events cap. */
  override?: boolean;
}

export function fetchTelemetryEvents(
  unitId: string,
  params: EventsParams,
  opts?: TelemetryRequestOptions,
): Promise<EventsResponse> {
  const url = `${BASE}/units/${encodeURIComponent(unitId)}/events` + qs({
    from: params.from,
    to: params.to,
    kinds: params.kinds?.join(","),
    states: params.states?.join(","),
    limit: params.limit,
    order: params.order,
    cursor: params.cursor,
    include_raw: params.include_raw,
    override: params.override,
  });
  return runTelemetryQuery<EventsResponse>(url, opts);
}

export interface CoverageParams {
  from: string;
  to: string;
  granularity?: "hour" | "day";
}

export function fetchTelemetryCoverage(
  unitId: string,
  params: CoverageParams,
  opts?: TelemetryRequestOptions,
): Promise<CoverageResponse> {
  const url = `${BASE}/units/${encodeURIComponent(unitId)}/coverage` + qs({
    from: params.from,
    to: params.to,
    granularity: params.granularity,
  });
  return runTelemetryQuery<CoverageResponse>(url, opts);
}

export interface SeriesParams {
  from: string;
  to: string;
  metrics?: TelemetryMetric[];
  bucket?: string;
}

export function fetchTelemetrySeries(
  unitId: string,
  params: SeriesParams,
  opts?: TelemetryRequestOptions,
): Promise<SeriesResponse> {
  const url = `${BASE}/units/${encodeURIComponent(unitId)}/series` + qs({
    from: params.from,
    to: params.to,
    metrics: params.metrics?.join(","),
    bucket: params.bucket,
  });
  return runTelemetryQuery<SeriesResponse>(url, opts);
}

export interface RawWindowParams {
  from: string;
  to: string;
  /** `src_file:src_line` pairs — addresses exact rows, does not make it cheaper. */
  refs?: string[];
  limit?: number;
  order?: "asc" | "desc";
  include_raw?: boolean;
  cursor?: string | null;
}

/** Admin only. The one endpoint that returns raw protocol frames. */
export function fetchRawWindow(
  unitId: string,
  params: RawWindowParams,
  opts?: TelemetryRequestOptions,
): Promise<RawWindowResponse> {
  const url = `${BASE}/units/${encodeURIComponent(unitId)}/raw-window` + qs({
    from: params.from,
    to: params.to,
    refs: params.refs?.join(","),
    limit: params.limit,
    order: params.order,
    include_raw: params.include_raw,
    cursor: params.cursor,
  });
  return runTelemetryQuery<RawWindowResponse>(url, opts);
}

/* ------------------------------------------------------------------ */
/*  First screen                                                       */
/* ------------------------------------------------------------------ */

export interface TelemetryWindowParams {
  from: string;
  to: string;
  kinds: TelemetryEventKind[];
  metrics: TelemetryMetric[];
  limit?: number;
}

export interface TelemetryWindowResult {
  /** compact = one /diagnostics scan; split = the per-view endpoints. */
  source: "diagnostics" | "split";
  range: { from: string; to: string };
  coverage: CoverageCell[];
  events: TelemetryEvent[];
  series: SeriesPoint[];
  /** Only /diagnostics computes it; wide windows derive what they can. */
  summary: TelemetrySummary | null;
  nextCursor: string | null;
  meta: TelemetryMeta;
  /** Every scan that paid for this screen — one in compact mode, three in split. */
  queries: TelemetryQueryMeta[];
  totalEvents: number | null;
}

export function rangeHours(from: string, to: string): number {
  return (new Date(to).getTime() - new Date(from).getTime()) / 3_600_000;
}

export function rangeMinutes(from: string, to: string): number {
  return (new Date(to).getTime() - new Date(from).getTime()) / 60_000;
}

/* ------------------------------------------------------------------ */
/*  Scan shape                                                         */
/* ------------------------------------------------------------------ */

/**
 * What a support view asks the lake for, once, per window.
 *
 * The scan is a superset on purpose: every standard event kind and every
 * charted metric come back in the one response, so the toggles above the table
 * are a `filter()` over rows already in memory instead of a new Athena bill per
 * click. Widening the scan costs nothing — the compact scan reads the same
 * columns of the same partitions whichever kinds the UI happens to show — while
 * narrowing it turns each toggle into a paid query.
 */
/** The kinds the events table offers as toggles. Service frames are not one of
 *  them: they are not support events and have a section of their own. */
export const VIEW_EVENT_KINDS: TelemetryEventKind[] = [
  "state_change",
  "command",
  "anomaly",
  "gap",
];

export const SCAN_EVENT_KINDS: TelemetryEventKind[] = [
  ...VIEW_EVENT_KINDS,
  // Service frames ride the same scan — in compact mode the rows are already
  // read, so deriving them costs nothing and the Service section never buys a
  // query of its own.
  "service",
];

export const SCAN_METRICS: TelemetryMetric[] = [
  "current",
  "voltage",
  "temperature",
  "frequency",
  // Same rows, same columns, same partitions — and it is what makes both the
  // energy chart and (with voltage x current) the power chart free to toggle.
  "session_energy",
];

/**
 * `sample` is the one toggle that is NOT a view filter.
 *
 * Every archived row is thousands per day rather than tens, so it is left out
 * of the standard scan — and a window fetched without it simply does not hold
 * the rows to show, which is why flipping it has to buy a new query.
 */
export type TelemetryScanShape = "standard" | "with_samples";

export function scanShapeOf(kinds: readonly TelemetryEventKind[]): TelemetryScanShape {
  return kinds.includes("sample") ? "with_samples" : "standard";
}

export function scanKindsFor(shape: TelemetryScanShape): TelemetryEventKind[] {
  return shape === "with_samples" ? [...SCAN_EVENT_KINDS, "sample"] : [...SCAN_EVENT_KINDS];
}

/**
 * One window, four views.
 *
 * Under the /diagnostics cap this is a SINGLE scan feeding coverage, events,
 * series and summary — Athena bills a 10 MB floor per query, so four separate
 * requests over one window cost four times the same read. Past the cap
 * /diagnostics refuses (correctly: the compact window is what makes it cheap),
 * and the per-view endpoints aggregate in SQL instead.
 */
export async function fetchTelemetryWindow(
  unitId: string,
  params: TelemetryWindowParams,
  opts?: TelemetryRequestOptions,
): Promise<TelemetryWindowResult> {
  const { from, to, kinds, metrics, limit } = params;

  if (rangeHours(from, to) <= DIAGNOSTICS_MAX_HOURS) {
    const body = await fetchDiagnostics(unitId, { from, to, kinds, metrics, limit }, opts);
    return {
      source: "diagnostics",
      range: body.range,
      coverage: body.coverage ?? [],
      events: body.events ?? [],
      series: body.series ?? [],
      summary: body.summary ?? null,
      nextCursor: body.next_cursor ?? null,
      meta: body.meta,
      queries: [body.meta.query],
      totalEvents: body.meta.total_events ?? null,
    };
  }

  const granularity =
    rangeHours(from, to) > COVERAGE_HOUR_GRANULARITY_MAX_HOURS ? "day" : "hour";
  const [coverage, series, events] = await Promise.all([
    fetchTelemetryCoverage(unitId, { from, to, granularity }, opts),
    fetchTelemetrySeries(unitId, { from, to, metrics }, opts),
    fetchTelemetryEvents(unitId, { from, to, kinds, limit, order: "desc" }, opts),
  ]);

  return {
    source: "split",
    range: events.range,
    coverage: coverage.data ?? [],
    events: events.data ?? [],
    series: series.data ?? [],
    // No summary in wide mode: it is derived from the compact scan's samples,
    // and inventing one from a single events page would be a count that looks
    // total and is not.
    summary: null,
    nextCursor: events.next_cursor ?? null,
    meta: events.meta,
    queries: [coverage.meta.query, series.meta.query, events.meta.query],
    totalEvents: events.meta.total_events ?? null,
  };
}
